/*
 * The CustomerConfirmServiceStep component is the step in customer SO authorization flow where services are
 * viewed and confirmed.
 * 
 * This takes the following props:
 *   auth                 - authentication object
 *   account              - customer for which this SO is being created
 *   assignedStep
 *   activeStep
 *   handleBack
 *   handleNext
 */

import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Typography,
         TableContainer, Table, TableBody, TableCell,
         TableHead, TableRow } from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import AppBindStyles from '../../AppBindStyles';

import ServiceTableRow from '../suborder/ServiceTableRow';

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  },
  accountLogo: {
    float: 'left',
    marginRight: 15
  },
  button: {
    width: 52,
  },
  input: {
    display: 'none',
  },
  newServiceRow: {
    // backgroundColor: yellow[100],
    borderLeft: 'solid',
    borderLeftWidth: 3,
    borderLeftColor: '#7D54EA;',
  },
  existingServiceRow: {
    marginLeft: 6
  },
  terms: {
    maxWidth: 170,
    minWidth: 170
  },
  serviceCell: {
    minWidth: 168
  }
});

const StyledTable = styled(Table) (({ theme }) => ({
  borderWidth: 1,
  borderStyle: 'solid',
  //borderColor: theme.palette.colors.secondary[200],
  borderColor: theme.palette.divider,
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: theme.palette.common.black,
    // color: theme.palette.common.white,
    // backgroundColor: theme.palette.colors.secondary[200],
    backgroundColor: theme.palette.divider,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  /*
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  */
  height: 50, // Make the row heights consistent.  Smaller than this could be better, but then padding needs to be adjusted.
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

class CustomerConfirmServiceStep extends React.Component {
  
  isBackDisabled() {
    if (this.props.activeStep === 0)
      return true;

    return false;
  }

  isNextDisabled() {
    if (this.props.activeStep === this.props.assignedStep) {
      return false;
    }
    return false;
  }

  render() {
    const classes = this.props.classes;

    let displayOnly = false;
    if (this.props.activeStep > this.props.assignedStep) {
      displayOnly = true;
    }

    let theAccount = this.props.account;

    let subscriptionsToDisplay = [];
    for (let i = 0; i < theAccount.customerServiceTsByCustomerAccountId.nodes.length; i++) {
      let service = theAccount.customerServiceTsByCustomerAccountId.nodes[i];
      //exclude cancelled services
      if (service.serviceStatus !== 2) {
        if (service.billingFrequency === 0) {
          //console.log(service.soAccepted);
          if (service.soAccepted === 0) {
            subscriptionsToDisplay.push(service);
          }
        } else {
          subscriptionsToDisplay.push(service);
        }
      }
    }

    if (subscriptionsToDisplay.length > 0) {
      subscriptionsToDisplay.sort(function(a, b) {
        if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
        if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
        return 0;
      });
    }

    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Grid container spacing={0} direction="row" justifyContent="space-between" >
            <Grid item xs={12}>
              <Typography variant="h6">
                Services for your project
              </Typography>
              </Grid>
            <Grid item xs={12}>
                <Typography variant="body2">We need your authorization to bill you for services we're providing for your project.</Typography>
            </Grid>
          </Grid>
        </Grid>

        {subscriptionsToDisplay.length > 0 && 
        <Grid item xs={12}>
          <TableContainer>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableCell className={classes.serviceCell} sx={{ borderTopLeftRadius: 8}}>
                    Service
                  </StyledTableCell>
                  <StyledTableCell sx={{ borderTopRightRadius: 8}}>
                    Terms
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptionsToDisplay.map(n => {
                  return (
                    <ServiceTableRow 
                      key={n.customerAppId}
                      service={n}
                      styledTableRow={StyledTableRow}
                      styledTableCell={StyledTableCell}
                      editable={false}
                      soContext="customerView"
                    />
                  );
                })}
              </TableBody>
            </StyledTable>
          </TableContainer>   
        </Grid>}

        {displayOnly === false && this.props.activeStep === this.props.assignedStep &&
        <Grid item xs={12}>
          { !this.isBackDisabled() &&
          <Button
            disabled={this.isBackDisabled()}
            color="primary" variant="outlined"
            onClick={() => this.props.handleBack()}
            style={AppBindStyles.bottomButton}>
            Back
          </Button>}

          <Button
            color="primary" variant="contained"
            disabled={this.isNextDisabled()}
            onClick={() => { this.props.handleNext()}}
            style={AppBindStyles.bottomButton}>
              Confirm
          </Button>
        </Grid>}               
      </Grid>
    );
  }
}

CustomerConfirmServiceStep.propTypes = {
  assignedStep: PropTypes.number.isRequired,
  activeStep: PropTypes.number.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
};

export default withStyles(styleSheet)(CustomerConfirmServiceStep);
